/*
 * Auth Service
 * Features: Auth state tracking, token refresh, login/logout helpers, and CRM API token bootstrap
 * Implementation notes:
 * - Uses httpOnly refresh token cookie and in-memory access token cache
 * - Proactive refresh and visibility refresh share a single refresh gate
 * Recent changes:
 * - Centralized refresh gating with ensureRefresh and typed refresh errors
 * - Consolidated post-login return flow to a single last-attempted URL path
 * - Removed auth redirects/withAuth flow; added redirectToLogin helper
 * - Re-read current auth state after async refresh boundaries to avoid stale narrowing and invalid merges
 * - Added CRM access token bootstrap helper backed by /api/auth/env
 * - Added selectable CRM JWT TTL support with a 1-day default for the token bootstrap flow
 * - Bound CRM access token lifecycle to server-stored PAT status and revocation
 */

type CrmAccessTokenTtlOption = '1h' | '1d' | '1y';

const DEFAULT_CRM_ACCESS_TOKEN_TTL_OPTION: CrmAccessTokenTtlOption = '1d';
interface UserData {
  email: string;
  name: string;
  picture: string;
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number; // Timestamp when token expires
  locale: string;
  emailVerified: boolean;
  isAuthorized?: boolean;
}

let userData: UserData | null = null;

// Track a single refresh in flight so callers can await it.
let refreshInProgress: Promise<void> | null = null;

interface GoogleUserInfo {
  email?: string;
  name?: string;
  picture?: string;
  locale?: string;
  email_verified?: boolean;
}

class RefreshFailedError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'RefreshFailedError';
    this.status = status;
  }
}

async function ensureRefresh(): Promise<void> {
  if (!refreshInProgress) {
    refreshInProgress = refreshAccessToken()
      .finally(() => {
        refreshInProgress = null;
      });
  }
  await refreshInProgress;
}

// Parse cookies from document.cookie
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

// Set cookie with options
function setCookie(name: string, value: string, options: Record<string, any> = {}): void {
  let cookie = `${name}=${encodeURIComponent(value)}`;

  if (options.maxAge) {
    cookie += `; max-age=${options.maxAge}`;
  }
  // Only set domain if explicitly provided - avoid automatic domain settings
  if (options.domain) {
    cookie += `; domain=${options.domain}`;
  }
  if (options.path) {
    cookie += `; path=${options.path}`;
  }
  if (options.expires) {
    cookie += `; expires=${options.expires}`;
  }
  if (options.secure) {
    cookie += '; secure';
  }
  if (options.sameSite) {
    cookie += `; samesite=${options.sameSite}`;
  }

  document.cookie = cookie;
}

// Delete a cookie with matching attributes
function deleteCookie(name: string): void {
  // Include all necessary attributes that match how cookies were set
  setCookie(name, '', {
    maxAge: -1,
    path: '/',
    expires: new Date(0).toUTCString(),
    secure: true,
    sameSite: 'Lax'
    // Removed domain attribute to match how cookies were originally set
  });
}

function isTokenExpired(): boolean {
  initializeAuth(); // Make sure we have latest userData

  // Return true if no userData or no expiresAt timestamp
  if (!userData || !userData.expiresAt) {
    return true;
  }

  // Check if token expires in the next 5 minutes
  return Date.now() + 5 * 60 * 1000 >= userData.expiresAt;
}

function handlePostLoginNavigation() {
  const shouldAutoNavigate = localStorage.getItem('shouldAutoNavigate');
  if (shouldAutoNavigate === 'true') {
    localStorage.removeItem('shouldAutoNavigate'); // Clear the flag
    // Use the last attempted URL or default to the app root.
    const lastAttemptedUrl = localStorage.getItem('lastAttemptedUrl') || '/';
    localStorage.removeItem('lastAttemptedUrl');

    // Prevent unnecessary reload if we are already at the target URL
    if (window.location.pathname + window.location.search !== lastAttemptedUrl) {
      window.location.href = lastAttemptedUrl;
    }
  }
}

function initializeAuth(): void {
  // Check for existing auth data in cookies
  const userDataCookie = getCookie('userData');

  if (userDataCookie) {
    try {
      userData = JSON.parse(userDataCookie);
      // Check for auto-navigation after successful auth
      handlePostLoginNavigation();
    } catch (error) {
      userData = null;
    }
  }
}

function isAuthenticated(): boolean {
  initializeAuth(); // Always check for latest auth data
  // A user is authenticated if userData exists and email and emailVerified
  // We DO NOT check accessToken here as that's only needed for API calls
  // This allows unauthorized users to still be considered "authenticated" and see the access denied page
  return userData !== null &&
    !!userData.email &&
    !!userData.emailVerified;
}

function persistUserData(updatedUserData: UserData): void {
  userData = updatedUserData;

  setCookie('userData', JSON.stringify(updatedUserData), {
    path: '/',
    maxAge: 30 * 24 * 60 * 60,
    secure: true,
    sameSite: 'Lax'
  });

  dispatchAuthRefreshEvent();
}

function getUserData(): UserData | null {
  initializeAuth(); // Always check for latest auth data
  return userData;
}

function isAuthorizedUser(currentUserData: UserData | null): currentUserData is UserData {
  return currentUserData !== null && currentUserData.isAuthorized !== false;
}

function hasAuthorizedAccessToken(currentUserData: UserData | null): currentUserData is UserData & { accessToken: string } {
  return isAuthorizedUser(currentUserData) && !!currentUserData.accessToken;
}

async function ensureUserProfile(forceRefresh = false): Promise<void> {
  let currentUserData = getUserData();

  if (!hasAuthorizedAccessToken(currentUserData)) {
    return;
  }

  if (!forceRefresh && currentUserData.picture && currentUserData.name && currentUserData.locale) {
    return;
  }

  try {
    if (isTokenExpired()) {
      await ensureRefresh();
      currentUserData = getUserData();
      if (!hasAuthorizedAccessToken(currentUserData)) {
        return;
      }
    }

    const response = await fetch('https://openidconnect.googleapis.com/v1/userinfo', {
      headers: {
        Authorization: `Bearer ${currentUserData.accessToken}`
      }
    });

    if (!response.ok) {
      return;
    }

    const profile = await response.json() as GoogleUserInfo;
    currentUserData = getUserData();
    if (!isAuthorizedUser(currentUserData)) {
      return;
    }

    const updatedUserData: UserData = {
      ...currentUserData,
      email: currentUserData.email || profile.email || '',
      name: forceRefresh ? (profile.name || currentUserData.name || '') : (currentUserData.name || profile.name || ''),
      picture: forceRefresh ? (profile.picture || currentUserData.picture || '') : (currentUserData.picture || profile.picture || ''),
      locale: forceRefresh ? (profile.locale || currentUserData.locale || '') : (currentUserData.locale || profile.locale || ''),
      emailVerified: currentUserData.emailVerified || profile.email_verified || false
    };

    if (
      updatedUserData.email !== currentUserData.email ||
      updatedUserData.name !== currentUserData.name ||
      updatedUserData.picture !== currentUserData.picture ||
      updatedUserData.locale !== currentUserData.locale ||
      updatedUserData.emailVerified !== currentUserData.emailVerified
    ) {
      persistUserData(updatedUserData);
    }
  } catch (error) {
    console.warn('Failed to backfill Google user profile', error);
  }
}

function isAuthorized(): boolean {
  initializeAuth(); // Always check for latest auth data
  return userData !== null && userData.isAuthorized !== false;
}

async function login(): Promise<void> {
  window.location.href = '/api/auth';
}

// Function to dispatch auth refresh event
function dispatchAuthRefreshEvent(): void {
  const event = new CustomEvent('AUTH_REFRESH');
  window.dispatchEvent(event);
}

// Handle service worker messages for token updates
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'AUTH_REFRESH' && event.data?.payload) {
      const { accessToken, expiresIn } = event.data.payload;
      const currentUserData = getUserData();

      // Early return if user data is invalid
      if (!accessToken || !isAuthorizedUser(currentUserData)) {
        return;
      }

      // Update user data - at this point we know userData is not null
      const updatedUserData: UserData = {
        ...currentUserData,
        accessToken,
        expiresAt: Date.now() + (expiresIn * 1000)
      };

      persistUserData(updatedUserData);
    }
  });
}

// Initialize auth state when module is loaded
initializeAuth();

// Proactive token refresh interval (check every 10 minutes)
const TOKEN_REFRESH_CHECK_INTERVAL = 10 * 60 * 1000; // 10 minutes
let proactiveRefreshTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Start proactive token refresh - checks token expiry periodically and refreshes before it expires.
 * This ensures tokens are refreshed even when the user is idle.
 */
function startProactiveTokenRefresh(): void {
  // Clear any existing timer
  if (proactiveRefreshTimer) {
    clearInterval(proactiveRefreshTimer);
  }

  // Check immediately, then set up interval
  const checkAndRefresh = async () => {
    // Only refresh if user is authenticated and authorized
    if (!userData || userData.isAuthorized === false) {
      return;
    }

    // Check if token will expire in the next 15 minutes (slightly more aggressive than the 5-min window)
    const refreshWindow = 15 * 60 * 1000; // 15 minutes
    if (userData.expiresAt && Date.now() + refreshWindow >= userData.expiresAt) {
      console.log('Proactive token refresh: Token expiring soon, refreshing...');
      try {
        await ensureRefresh();
        console.log('Proactive token refresh: Success');
      } catch (error) {
        console.warn('Proactive token refresh failed:', error);
        // Don't logout here - let the normal auth flow handle it on next API call
      }
    }
  };

  // Run initial check after a short delay to avoid blocking initial load
  setTimeout(checkAndRefresh, 5000);

  // Set up periodic check
  proactiveRefreshTimer = setInterval(checkAndRefresh, TOKEN_REFRESH_CHECK_INTERVAL);
}

/**
 * Stop proactive token refresh (called on logout)
 */
function stopProactiveTokenRefresh(): void {
  if (proactiveRefreshTimer) {
    clearInterval(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}

function redirectToLogin(): void {
  const attemptedUrl = window.location.pathname + window.location.search;
  try {
    localStorage.setItem('lastAttemptedUrl', attemptedUrl);
    localStorage.setItem('shouldAutoNavigate', 'true');
  } catch (error) {
    console.warn('Unable to store post-login navigation state', error);
  }

  // Stop proactive refresh since auth is invalid
  stopProactiveTokenRefresh();

  window.location.href = '/api/auth';
}

// Start proactive refresh if user is already authenticated
// Use getUserData() to get current state
const initialUserData = getUserData();
if (initialUserData && initialUserData.isAuthorized !== false) {
  startProactiveTokenRefresh();
}

// Also listen for visibility changes to refresh token when user returns to tab
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    const currentUser = getUserData();
    if (document.visibilityState === 'visible' && currentUser && currentUser.isAuthorized !== false) {
      // Check and refresh token when user returns to tab
      if (isTokenExpired()) {
        console.log('Tab visible: Token expired, refreshing...');
        void ensureRefresh()
          .catch(error => console.warn('Token refresh on tab focus failed:', error));
      }
    }
  });
}

function logout(): void {
  // Stop proactive token refresh
  stopProactiveTokenRefresh();

  // Clear auth-related cookies using our helper function
  // This will respect the exact same path/security settings as when they were set
  deleteCookie('userData');
  deleteCookie('refreshToken'); // Attempt to clear HTTP-only cookie

  // Clear memory
  userData = null;

  // Clear localStorage items
  try {
    localStorage.removeItem('accounts-columns');
    localStorage.removeItem('accounts-search');
    localStorage.removeItem('accounts-filter');
    localStorage.removeItem('lastAttemptedUrl');
    localStorage.removeItem('shouldAutoNavigate');
  } catch (e) {
    // Ignore errors with localStorage
  }

  // Redirect to logout endpoint which will clear server-side session
  window.location.href = '/api/auth/logout';
}

async function refreshAccessToken(): Promise<void> {
  const currentUserData = getUserData();

  // Don't try to refresh tokens for unauthorized users
  if (currentUserData?.isAuthorized === false) {
    throw new RefreshFailedError('User not authorized', 403);
  }

  if (!currentUserData) {
    throw new RefreshFailedError('User not authenticated', 401);
  }

  const MAX_RETRIES = 3;
  const INITIAL_RETRY_DELAY = 1000; // 1 second
  let retryCount = 0;
  let lastError: RefreshFailedError | null = null;

  while (retryCount <= MAX_RETRIES) {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin', // Important to include credentials for cookies
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new RefreshFailedError('Refresh token invalid', 401);
        }

        throw new RefreshFailedError(`Token refresh failed with status: ${response.status}`, response.status);
      }

      const { accessToken, expiresIn } = await response.json();
      const latestUserData = getUserData();

      // Check for valid userData and access token
      if (!accessToken || !isAuthorizedUser(latestUserData)) {
        throw new RefreshFailedError('User data is no longer valid', 401);
      }

      // Update user data - at this point we know userData is not null
      const updatedUserData: UserData = {
        ...latestUserData,
        accessToken,
        expiresAt: Date.now() + (expiresIn * 1000)
      };

      persistUserData(updatedUserData);

      // Success, exit the retry loop
      return;

    } catch (error) {
      const isNetworkError = error instanceof TypeError;
      const refreshError = error instanceof RefreshFailedError
        ? error
        : isNetworkError
          ? new RefreshFailedError('Token refresh failed due to network error')
          : new RefreshFailedError(
            error instanceof Error ? error.message : 'Unknown error during token refresh',
            400
          );
      lastError = refreshError;

      const status = refreshError.status;
      const retryable = isNetworkError || (status !== undefined && status >= 500);

      if (!retryable) {
        throw refreshError;
      }

      // If this was our last retry attempt, break out of the loop
      if (retryCount === MAX_RETRIES) {
        break;
      }

      // Exponential backoff with jitter
      const delay = INITIAL_RETRY_DELAY * Math.pow(2, retryCount) + Math.random() * 1000;
      console.warn(`Token refresh attempt ${retryCount + 1} failed, retrying in ${Math.round(delay)}ms`, lastError);

      // Wait before next retry
      await new Promise(resolve => setTimeout(resolve, delay));
      retryCount++;
    }
  }

  // All retries failed or user data became invalid
  console.error(`Token refresh failed after ${MAX_RETRIES} attempts`, lastError);
  throw lastError ?? new RefreshFailedError('Token refresh failed');
}

async function getAuthHeaders(): Promise<HeadersInit> {
  let currentUserData = getUserData();

  if (!currentUserData) {
    throw new Error('Unauthorized');
  }

  // Don't try to refresh tokens for unauthorized users
  if (currentUserData.isAuthorized === false) {
    throw new Error('User not authorized');
  }

  // Only refresh if token is expired or about to expire
  if (isTokenExpired()) {
    await ensureRefresh();
    // Get fresh user data after refresh
    currentUserData = getUserData();
    if (!hasAuthorizedAccessToken(currentUserData)) {
      throw new Error('Unauthorized - user data lost after refresh');
    }
  }

  return {
    'X-Google-Auth': `Bearer ${currentUserData.accessToken}`
  };
}

async function getCrmAccessToken(ttlOption: CrmAccessTokenTtlOption = DEFAULT_CRM_ACCESS_TOKEN_TTL_OPTION): Promise<string> {
  const headers = await getAuthHeaders();
  const response = await fetch(`/api/auth/env?ttl=${encodeURIComponent(ttlOption)}`, {
    method: 'GET',
    headers,
    credentials: 'same-origin'
  });

  if (!response.ok) {
    throw new Error(`Access token request failed with status ${response.status}`);
  }

  const envText = await response.text();
  const tokenLine = envText
    .split(/\r?\n/)
    .find((line) => line.startsWith('CRM_ACCESS_TOKEN='));

  const token = tokenLine?.slice('CRM_ACCESS_TOKEN='.length).trim();
  if (!token) {
    throw new Error('CRM_ACCESS_TOKEN not found in response');
  }

  return token;
}

async function getCrmAccessTokenStatus(): Promise<{ hasPat: boolean }> {
  const headers = await getAuthHeaders();
  const response = await fetch('/api/data/users/me/pat', {
    method: 'GET',
    headers: {
      ...headers,
      'Content-Type': 'application/json'
    },
    credentials: 'same-origin'
  });

  const data = await handleResponse(response);
  return {
    hasPat: !!data?.hasPat
  };
}

async function clearCrmAccessToken(): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch('/api/data/users/me/pat', {
    method: 'DELETE',
    headers: {
      ...headers,
      'Content-Type': 'application/json'
    },
    credentials: 'same-origin'
  });

  await handleResponse(response);
}

async function handleResponse(response: Response) {
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  try {
    const text = await response.text();
    // Return null for empty responses
    if (!text) return null;
    // Try to parse as JSON
    return JSON.parse(text);
  } catch (error) {
    // If parsing fails, return null
    return null;
  }
}

export type { UserData };
export {
  DEFAULT_CRM_ACCESS_TOKEN_TTL_OPTION,
  isAuthenticated,
  isAuthorized,
  getUserData,
  login,
  logout,
  getAuthHeaders,
  getCrmAccessToken,
  ensureRefresh,
  redirectToLogin,
  handleResponse,
  deleteCookie,
  RefreshFailedError,
  ensureUserProfile,
  getCrmAccessTokenStatus,
  clearCrmAccessToken,
};

export type { CrmAccessTokenTtlOption };
